import { AdminReview } from "@/hooks/useAdminReviews";
import { useReviewModeration } from "@/hooks/useReviewModeration";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import ReviewStatusBadge from "./ReviewStatusBadge";
import { Star, CheckCircle, ThumbsUp, Trash2, Link as LinkIcon } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";

interface ReviewDetailsDialogProps {
  review: AdminReview | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ReviewDetailsDialog = ({ review, open, onOpenChange }: ReviewDetailsDialogProps) => {
  const { approveReview, rejectReview, deleteReview, isLoading } = useReviewModeration();

  if (!review) return null;

  const handleApprove = () => {
    approveReview(review.id);
    onOpenChange(false);
  };

  const handleReject = () => {
    rejectReview(review.id);
    onOpenChange(false);
  };

  const handleDelete = () => {
    if (confirm("Are you sure you want to permanently delete this review?")) {
      deleteReview(review.id);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Details</DialogTitle>
          <DialogDescription>
            View full review information and take moderation actions
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Status and Rating */}
          <div className="flex items-center justify-between">
            <ReviewStatusBadge status={review.status} />
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`h-5 w-5 ${
                    i < review.rating
                      ? "fill-yellow-400 text-yellow-400"
                      : "fill-muted text-muted-foreground"
                  }`}
                />
              ))}
              <span className="ml-2 text-sm font-medium">{review.rating}/5</span>
            </div>
          </div>

          {/* Product Info */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold mb-2">Product</h3>
            <div className="flex items-center gap-3">
              {review.product_image && (
                <img
                  src={review.product_image}
                  alt={review.product_name || "Product"}
                  className="h-16 w-16 rounded-lg object-cover"
                />
              )}
              <div className="flex-1">
                <Link 
                  to={`/product/${review.product_slug || review.product_id}`}
                  className="text-base font-medium hover:underline flex items-center gap-2"
                >
                  {review.product_name || "Unknown Product"}
                  <LinkIcon className="h-3 w-3" />
                </Link>
                <p className="text-sm text-muted-foreground">Product ID: {review.product_id.slice(0, 8)}...</p>
              </div>
            </div>
          </div>

          {/* Reviewer Info */}
          <div className="rounded-lg border bg-card p-4">
            <h3 className="text-sm font-semibold mb-2">Reviewer</h3>
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarImage src={review.reviewer_avatar} />
                <AvatarFallback>{review.reviewer_name[0]?.toUpperCase()}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <p className="font-medium">{review.reviewer_name}</p>
                {review.reviewer_email && (
                  <p className="text-sm text-muted-foreground">{review.reviewer_email}</p>
                )}
                <div className="flex items-center gap-2 mt-1">
                  {review.verified_purchase && (
                    <Badge variant="secondary" className="text-xs flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Verified Purchase
                    </Badge>
                  )}
                  {review.user_id ? (
                    <Badge variant="outline" className="text-xs">Registered User</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">Guest</Badge>
                  )}
                </div>
              </div>
            </div>
          </div>

          <Separator />

          {/* Review Content */}
          <div>
            {review.title && (
              <h3 className="text-lg font-semibold mb-2">{review.title}</h3>
            )}
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {review.content || "No review text provided"}
            </p>
          </div>

          <Separator />

          {/* Metadata */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Helpful Votes</p>
              <div className="flex items-center gap-1 font-medium">
                <ThumbsUp className="h-4 w-4" />
                {review.helpful_count}
              </div>
            </div>
            <div>
              <p className="text-muted-foreground">Featured</p>
              <p className="font-medium">{review.is_featured ? "Yes" : "No"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Submitted</p>
              <p className="font-medium">{format(new Date(review.created_at), "PPp")}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Updated</p>
              <p className="font-medium">{format(new Date(review.updated_at), "PPp")}</p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
          <div className="flex-1" />
          {review.status !== 'rejected' && (
            <Button
              variant="outline"
              onClick={handleReject}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Reject
            </Button>
          )}
          {review.status !== 'approved' && (
            <Button
              onClick={handleApprove}
              disabled={isLoading}
              className="w-full sm:w-auto"
            >
              Approve
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDetailsDialog;
